import { useMemo, useState } from "react";
import { api } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Moon, Sun, ShieldCheck } from "@phosphor-icons/react";

const DRIFT_THRESHOLD_DAYS = 90;
const MANAGING_ROLES = new Set(["partner", "principal", "managing_principal", "managing principal"]);

const fmtDate = (iso) => {
  if (!iso) return "—";
  try {
    return new Date(iso).toLocaleDateString(undefined, { month: "short", day: "numeric", year: "numeric" });
  } catch { return iso; }
};

/**
 * Local (per-engagement) surface for the drift nudge + snooze.
 *
 * Renders one of three states:
 *   • active-nudge   — emphasis is stale (>90d) and not snoozed → shows a
 *                      quiet ochre nudge with "Snooze 30d" (MP only) and
 *                      "Change primary emphasis" affordances.
 *   • active-snooze  — an MP has deliberately parked the nudge. Renders as a
 *                      muted card showing who + until when, with an
 *                      "Unsnooze" button (MP only).
 *   • fresh          — last approval is within the quarter → renders nothing.
 *
 * Governance choices baked in:
 *   • Snooze is MP-only; a 30-day fixed window; no permanent silencing.
 *   • Snoozes are recorded in `drift_snooze_events[]` (separate from the
 *     emphasis-history audit log) so history stays clean.
 *   • Language is a nudge, not an alert.
 */
export default function DriftNudgeCard({ engagementId, roadmap, onChangeRequested, onUpdated }) {
  const { user } = useAuth();
  const isMP = MANAGING_ROLES.has((user?.role || "").toLowerCase());
  const [busy, setBusy] = useState(false);

  const state = useMemo(() => {
    if (!roadmap || !roadmap.current_phase) return { kind: "hidden" };
    const history = roadmap.emphasis_history || [];
    if (history.length === 0) return { kind: "hidden" };
    const last = history[history.length - 1];
    const lastAt = last.effective_date || last.approved_at;
    if (!lastAt) return { kind: "hidden" };
    const days = Math.floor((Date.now() - new Date(lastAt).getTime()) / 86_400_000);

    const snoozedUntil = roadmap.drift_snoozed_until;
    const snoozeActive = snoozedUntil && new Date(snoozedUntil).getTime() > Date.now();
    if (snoozeActive) {
      return {
        kind: "snoozed",
        snoozedUntil,
        snoozedBy: roadmap.drift_snooze_last?.snoozed_by_name || roadmap.drift_snooze_last?.snoozed_by,
        days,
      };
    }
    if (days > DRIFT_THRESHOLD_DAYS) {
      return { kind: "nudge", days, lastApprovedBy: last.approved_by_name || last.approved_by };
    }
    return { kind: "hidden" };
  }, [roadmap]);

  if (state.kind === "hidden") return null;

  const snooze = async () => {
    setBusy(true);
    try {
      const { data } = await api.post(`/engagements/${engagementId}/roadmap/snooze-drift`);
      toast.success("Drift nudge snoozed for 30 days — we'll re-ask then.");
      onUpdated?.(data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Snooze failed");
    } finally { setBusy(false); }
  };

  const unsnooze = async () => {
    setBusy(true);
    try {
      const { data } = await api.post(`/engagements/${engagementId}/roadmap/unsnooze-drift`);
      toast.info("Drift nudge unmuted.");
      onUpdated?.(data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Unsnooze failed");
    } finally { setBusy(false); }
  };

  if (state.kind === "snoozed") {
    return (
      <div className="border border-border/60 bg-secondary/30 p-4 flex flex-wrap items-center justify-between gap-3" data-testid="drift-snoozed-card">
        <div className="flex items-start gap-2.5">
          <Moon size={16} weight="fill" className="text-muted-foreground shrink-0 mt-0.5" />
          <div>
            <div className="text-[0.625rem] font-mono uppercase tracking-widest text-muted-foreground">
              Drift nudge snoozed
            </div>
            <div className="text-sm mt-0.5" style={{ fontFamily: "Georgia, serif" }}>
              We won't remind you about this emphasis until <span className="text-foreground font-semibold">{fmtDate(state.snoozedUntil)}</span>
              {state.snoozedBy ? <> · parked by <span className="text-primary/80">{state.snoozedBy}</span></> : null}.
            </div>
          </div>
        </div>
        {isMP && (
          <Button
            onClick={unsnooze}
            disabled={busy}
            variant="ghost"
            size="sm"
            className="rounded-none text-muted-foreground hover:text-primary"
            data-testid="drift-unsnooze-btn"
          >
            <Sun size={13} weight="bold" className="mr-1.5" /> Unmute
          </Button>
        )}
      </div>
    );
  }

  // nudge state
  return (
    <div className="border border-primary/40 bg-primary/5 p-5" data-testid="drift-nudge-card">
      <div className="flex items-start gap-3">
        <ShieldCheck size={18} weight="fill" className="text-primary shrink-0 mt-0.5" />
        <div className="flex-1">
          <div className="text-[0.625rem] font-mono uppercase tracking-[0.22em] text-primary">
            Emphasis drift · a soft nudge
          </div>
          <p className="mt-2 text-sm leading-relaxed" style={{ fontFamily: "Georgia, serif" }}>
            The primary emphasis on this engagement hasn't been re-affirmed by a Managing Principal in <span className="text-foreground font-semibold">{state.days} days</span>.
            {" "}It's worth a moment to ask: is this still true, or has the work quietly moved?
          </p>
        </div>
      </div>
      <div className="mt-4 flex flex-wrap justify-end gap-2">
        {isMP && (
          <>
            <Button
              onClick={snooze}
              disabled={busy}
              variant="ghost"
              className="rounded-none text-muted-foreground hover:text-primary"
              data-testid="drift-snooze-btn"
            >
              <Moon size={13} className="mr-1.5" /> Snooze 30 days
            </Button>
            <Button
              onClick={onChangeRequested}
              className="rounded-none bg-primary text-primary-foreground hover:bg-primary/90"
              data-testid="drift-change-emphasis-btn"
            >
              Change primary emphasis
            </Button>
          </>
        )}
        {!isMP && (
          <div className="text-[0.625rem] font-mono uppercase tracking-widest text-amber-500/90 border border-amber-500/40 px-3 py-1.5">
            Managing Principal action required
          </div>
        )}
      </div>
    </div>
  );
}
